import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { budgetAPI } from "../../services/api/budget.api";

const BudgetAdminApprovalPage = () => {
  const navigate = useNavigate();
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [rejectionDialog, setRejectionDialog] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [comment, setComment] = useState("");
  const [statusFilter, setStatusFilter] = useState("finance_approved");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchBudgets();
  }, [statusFilter]);

  const fetchBudgets = async () => {
    try {
      setLoading(true);
      const response = await budgetAPI.getBudgets({ status: statusFilter });
      if (response.data.success) {
        setBudgets(response.data.data.budgets || []);
      }
    } catch (error) {
      console.error("Error fetching budgets:", error);
      setError("Failed to fetch budgets");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setLoading(true);
      await budgetAPI.adminApproveBudget(selectedBudget.id, {
        action: "approve",
        comment: comment,
      });
      setSuccess("Budget approved successfully!");
      setApprovalDialog(false);
      setComment("");
      setSelectedBudget(null);
      fetchBudgets();
    } catch (error) {
      console.error("Error approving budget:", error);
      setError("Failed to approve budget");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!comment.trim()) {
      setError("Rejection comment is required");
      return;
    }

    try {
      setLoading(true);
      await budgetAPI.adminApproveBudget(selectedBudget.id, {
        action: "reject",
        comment: comment,
      });
      setSuccess("Budget rejected successfully!");
      setRejectionDialog(false);
      setComment("");
      setSelectedBudget(null);
      fetchBudgets();
    } catch (error) {
      console.error("Error rejecting budget:", error);
      setError("Failed to reject budget");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "default";
      case "submitted":
        return "warning";
      case "finance_approved":
        return "info";
      case "finance_rejected":
        return "error";
      case "admin_approved":
        return "success";
      case "admin_rejected":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "draft":
        return "Draft";
      case "submitted":
        return "Submitted";
      case "finance_approved":
        return "Finance Approved";
      case "finance_rejected":
        return "Finance Rejected";
      case "admin_approved":
        return "Admin Approved";
      case "admin_rejected":
        return "Admin Rejected";
      default:
        return status;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-TZ", {
      style: "currency",
      currency: "TZS",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getDepartmentName = (budget) => {
    return budget.department?.department_name || "N/A";
  };

  const getResponsiblePersonName = (budget) => {
    const person = budget.responsiblePerson;
    if (person) {
      return (
        `${person.first_name || ""} ${person.sur_name || ""}`.trim() || "N/A"
      );
    }
    return "N/A";
  };

  const getFinanceApproverName = (budget) => {
    const approver = budget.financeApprover;
    if (approver) {
      return (
        `${approver.first_name || ""} ${approver.sur_name || ""}`.trim() ||
        "N/A"
      );
    }
    return "N/A";
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Button onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            Back
          </Button>
          <Typography variant="h4">Admin Budget Approvals</Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchBudgets}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Review and approve/reject budgets that have been approved by Finance
        Managers. You can only approve budgets that are in "Finance Approved"
        status.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <FilterIcon />
            <Typography variant="h6">Filter</Typography>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="finance_approved">Finance Approved</MenuItem>
                <MenuItem value="admin_approved">Admin Approved</MenuItem>
                <MenuItem value="admin_rejected">Admin Rejected</MenuItem>
                <MenuItem value="submitted">Submitted</MenuItem>
                <MenuItem value="finance_rejected">Finance Rejected</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Budgets Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Budgets for Final Approval ({budgets.length})
          </Typography>

          {budgets.length === 0 ? (
            <Alert severity="info">
              No budgets found for the selected status.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Activity Name</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Responsible Person</TableCell>
                    <TableCell>Budget Period</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Finance Approver</TableCell>
                    <TableCell>Finance Approval Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {budgets.map((budget) => (
                    <TableRow key={budget.id}>
                      <TableCell>{budget.activity_name}</TableCell>
                      <TableCell>{getDepartmentName(budget)}</TableCell>
                      <TableCell>{getResponsiblePersonName(budget)}</TableCell>
                      <TableCell>
                        {budget.budgetPeriod ? budget.budgetPeriod.name : "N/A"}
                      </TableCell>
                      <TableCell>{formatCurrency(budget.amount)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(budget.status)}
                          color={getStatusColor(budget.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{getFinanceApproverName(budget)}</TableCell>
                      <TableCell>
                        {budget.finance_approved_at
                          ? formatDate(budget.finance_approved_at)
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedBudget(budget);
                                setDetailsDialog(true);
                              }}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          {budget.status === "finance_approved" && (
                            <>
                              <Tooltip title="Approve Budget">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => {
                                    setSelectedBudget(budget);
                                    setApprovalDialog(true);
                                  }}
                                >
                                  <ApproveIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject Budget">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setSelectedBudget(budget);
                                    setRejectionDialog(true);
                                  }}
                                >
                                  <RejectIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Approval Dialog */}
      <Dialog
        open={approvalDialog}
        onClose={() => setApprovalDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve Budget</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to approve this budget? This will be the final
            approval.
          </Typography>
          {selectedBudget && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">
                {selectedBudget.activity_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Amount: {formatCurrency(selectedBudget.amount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Department: {getDepartmentName(selectedBudget)}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Approval Comment (Optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add any comments about this final approval..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)}>Cancel</Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            color="success"
            disabled={loading}
          >
            {loading ? "Approving..." : "Approve Budget"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog
        open={rejectionDialog}
        onClose={() => setRejectionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Budget</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to reject this budget? This will be the final
            rejection.
          </Typography>
          {selectedBudget && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">
                {selectedBudget.activity_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Amount: {formatCurrency(selectedBudget.amount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Department: {getDepartmentName(selectedBudget)}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason (Required)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Please provide a reason for rejection..."
            required
            error={!comment.trim()}
            helperText={!comment.trim() ? "Rejection reason is required" : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialog(false)}>Cancel</Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={loading || !comment.trim()}
          >
            {loading ? "Rejecting..." : "Reject Budget"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Budget Details</DialogTitle>
        <DialogContent>
          {selectedBudget && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {selectedBudget.activity_name}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Department
                </Typography>
                <Typography variant="body1">
                  {getDepartmentName(selectedBudget)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Responsible Person
                </Typography>
                <Typography variant="body1">
                  {getResponsiblePersonName(selectedBudget)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Budget Period
                </Typography>
                <Typography variant="body1">
                  {selectedBudget.budgetPeriod
                    ? selectedBudget.budgetPeriod.name
                    : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Amount
                </Typography>
                <Typography variant="body1">
                  {formatCurrency(selectedBudget.amount)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={getStatusText(selectedBudget.status)}
                  color={getStatusColor(selectedBudget.status)}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body1">
                  {formatDate(selectedBudget.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Finance Approver
                </Typography>
                <Typography variant="body1">
                  {getFinanceApproverName(selectedBudget)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Finance Approval Date
                </Typography>
                <Typography variant="body1">
                  {selectedBudget.finance_approved_at
                    ? formatDate(selectedBudget.finance_approved_at)
                    : "N/A"}
                </Typography>
              </Grid>
              {selectedBudget.finance_approval_comment && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Finance Approval Comment
                  </Typography>
                  <Typography variant="body1">
                    {selectedBudget.finance_approval_comment}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1">
                  {selectedBudget.description || "No description provided"}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BudgetAdminApprovalPage;
